import {
    CREDIT_CARD_DOWN_PAYMENT,
    CASH_DOWN_PAYMENT,
    FIRST_MONTHLY_PAYMENT,
    OTHER_UPFRONT_COSTS,
    AMOUNTS_DUE_AT_LEASE_SIGNING,
    HOW_AMOUNTS_DUE_WILL_BE_PAID,
    NET_TRADE_IN_AMOUNT,
    AMOUNT_PAID_IN_CASH,
    SURFACE_PROTECTION,
    MI,
    MO
} from 'constants/pdsConstant'
import { numberToCurrencyFormat, numberFormat } from './number-format-util'
import { getDisclosures } from './disclosures-util'
const disclosures = getDisclosures()

const commonTitles = {
    AMOUNT_DUE_TITLE: 'Itemization Of Amount Due At Lease Signing Or Delivery',
    LEASE_DSP: 'Gross Capitalized Cost Breakdown',
    RETAIL_DSP: 'Vehicle Purchase Price',
    TAX_AND_FEES: 'Taxes and Fees',
    LEASE_VEHCILE_PAYMENT_INFO: 'Vehicle Lease Payment',
    TAX_CREDIT: 'Tax Credit'
}

function createDetail({
    mainTitle = null,
    name,
    key,
    preFormat = null,
    format = numberToCurrencyFormat,
    symbol = null,
    details = [],
    sign = null,
    style = []
}) {
    // We can add CustomTitle & Custom Fields & also creation of negative & postive
    return { mainTitle, name, key, symbol, preFormat, format, sign, details, style }
}

export const formatDynamiceName = (detailName, source) => {
    const detailsName = detailName === 'document.name' ? disclosures.documentFee.name : detailName
    const keys = detailsName ? detailsName.split('.') : null
    const value = keys ? keys.reduce((obj, key) => obj?.[key], source) : null
    return value != null ? value : detailsName
}

export const formatValue = (preFormat, format, value, symbol = '', sign = '') => {
    if (preFormat) {
        value = preFormat(value)
    }
    let formattedValue = format(value)
    if (formattedValue && sign) {
        formattedValue = `${sign}${formattedValue}`
    } else if (formattedValue && symbol) {
        formattedValue = `${formattedValue} ${symbol}`
    }
    return formattedValue
}

export const createCommonDetails = (mainTitle = null) => {
    return [
        createDetail({
            mainTitle: mainTitle,
            name: disclosures.dealerSellingPrice.name,
            key: 'pdsDealerSellingPrice.dealerSellingPrice',
            details: [
                createDetail({
                    name: disclosures.totalMsrp.name,
                    key: 'pdsDealerSellingPrice.totalMsrp',
                    details: [
                        createDetail({ name: disclosures.baseMsrp.name, key: 'pdsDealerSellingPrice.baseMsrp' }),
                        createDetail({
                            name: disclosures.totalOfOptions.name,
                            key: 'pdsDealerSellingPrice.totalOfOptions'
                        }),
                        createDetail({
                            name: disclosures.destinationAndDelivery.name,
                            key: 'pdsDealerSellingPrice.destinationAndDelivery'
                        })
                    ]
                }),
                createDetail({
                    name: disclosures.dealerAdjustments.name,
                    key: 'pdsDealerSellingPrice.dealerAdjustments'
                }),
                createDetail({ name: 'document.name', key: 'pdsDealerSellingPrice.documentFee' }),
                createDetail({
                    name: disclosures.totalDealerInstalledOptions.name,
                    key: 'pdsDealerSellingPrice.totalDealerInstalledOptions'
                }),
                createDetail({ name: disclosures.dealerDiscount.name, key: 'pdsDealerSellingPrice.dealerDiscount' })
            ]
        }),
        createDetail({
            name: disclosures.netTradeInAllowance.name,
            key: 'tradeInVehicle.netTradeInAllowance',
            preFormat: (value) => -+value || 0,
            details: [
                createDetail({ name: disclosures.grossAllowance.name, key: 'tradeInVehicle.grossAllowance' }),
                createDetail({ name: disclosures.payoffOwed.name, key: 'tradeInVehicle.payoffOwed' })
            ]
        }),
        createDetail({
            name: 'Remaining Lease Balance',
            key: 'tradeInVehicle.priorCreditOrLeaseOwedOnTrade'
        })
    ]
}

export const createTaxAndFeesDetails = (mainTitle = null, checkBorder = null) => {
    return [
        createDetail({
            mainTitle: mainTitle,
            name: disclosures.totalTaxesAndFees.name,
            key: 'taxesAndFees.totalTaxesAndFees',
            details: [
                createDetail({ name: disclosures.salesTax.name, key: 'taxesAndFees.salesTax' }),
                createDetail({ name: disclosures.licenseFee.name, key: 'taxesAndFees.licenseFee' }),
                createDetail({ name: disclosures.titleFee.name, key: 'taxesAndFees.titleFee' }),
                createDetail({ name: disclosures.registrationFee.name, key: 'taxesAndFees.registrationFee' }),
                createDetail({ name: disclosures.otherTaxesAndFees.name, key: 'taxesAndFees.otherTaxesAndFees' })
            ],
            style: { noBorderBottom: checkBorder ? 1 : 0 }
        })
    ]
}

const nonLeaseMappingDetails = (financeType = null) => {
    const nonLeaseDetails = [
        ...createCommonDetails(commonTitles.RETAIL_DSP),
        // Down payment
        createDetail({
            name: disclosures.cashDownPayment.name,
            details: [
                createDetail({ name: CASH_DOWN_PAYMENT, key: 'cashDownPayment', sign: '-' }),
                createDetail({ name: CREDIT_CARD_DOWN_PAYMENT, key: 'creditCardDownPayment', sign: '-' }),
                createDetail({ name: 'otherRebateDesc', key: 'otherRebate', sign: '-' })
            ]
        }),
        // Available Incentives
        createDetail({ name: disclosures.currentAvailableIncentives.name, key: 'currentAvailableIncentives' }),
        // Total Cost of Accessories
        createDetail({ name: disclosures.totalCostOfAccessories.name, key: 'totalCostOfAccessories' }),
        // Protection Plan
        createDetail({
            name: disclosures.insuranceWarranties.name,
            key: 'insuranceWarranties.totalOfInsuranceWarranties'
        }),
        // Pre Tax Price
        createDetail({
            name: disclosures.preTaxPrice.name,
            key: 'taxesAndFees.preTaxPrice',
            style: { noBorderBottom: 1, boldBorderTop: 1, textBold: 1 }
        }),
        // Taxes & Fees
        ...createTaxAndFeesDetails(commonTitles.TAX_AND_FEES, 1),
        // Tax Credit
        createDetail({
            mainTitle: commonTitles.TAX_CREDIT,
            name: disclosures.federalEVTaxCredit.name,
            key: 'taxesAndFees.federalEVTaxCredit',
            style: { noBorderBottom: 1 }
        }),
        // Amount Financed
        createDetail({
            name: financeType === 'Cash' ? disclosures.cashDueAtDealer.name : disclosures.amountFinanced.name,
            key: financeType === 'Cash' ? 'cashDueAtDealer' : 'amountFinanced',
            style: { noBorderBottom: 1, finalTotal: 1 }
        })
    ]
    return nonLeaseDetails
    // ... more mapping details
}

const leaseMappingDetails = () => {
    const leaseDetails = [
        ...createCommonDetails(commonTitles.LEASE_DSP),
        // Total Cost of Accessories
        createDetail({ name: disclosures.totalCostOfAccessories.name, key: 'totalCostOfAccessories' }),
        // Total Protection Plan
        createDetail({
            name: disclosures.insuranceWarranties.name,
            key: 'insuranceWarranties.totalOfInsuranceWarranties'
        }),
        // Total Taxes & Fees
        ...createTaxAndFeesDetails(),
        createDetail({ name: SURFACE_PROTECTION, key: 'surfaceProtection' }),
        // Total Gross Captaized cost
        createDetail({
            name: disclosures.grossCapCost.name,
            key: 'grossCapCost',
            style: { noBorderBottom: 1, borderTop: 1 }
        }),
        // Captilized cost Reduction
        createDetail({
            name: disclosures.totalCapitalizedCostReduction.name,
            key: 'capitalizedCostReduction.totalCapitalizedCostReduction',
            preFormat: (value) => -+value || -0
        }),
        //Adjusted Captalized Cost
        createDetail({
            name: disclosures.adjustedCapCost.name,
            key: 'adjustedCapCost',
            style: { noBorderBottom: 1, boldBorderTop: 1, textBold: 1 }
        }),
        //Vehicle Lease Payment
        createDetail({
            mainTitle: commonTitles.LEASE_VEHCILE_PAYMENT_INFO,
            name: null,
            details: [
                createDetail({ name: disclosures.basePayment.name, key: 'vehicleFinancing.basePayment' }),
                createDetail({ name: disclosures.monthlyTax.name, key: 'vehicleFinancing.monthlyTax' }),
                createDetail({ name: disclosures.monthlyPayment.name, key: 'vehicleFinancing.monthlyPayment' }),
                createDetail({
                    name: disclosures.annualMileage.name,
                    format: numberFormat,
                    symbol: MI,
                    key: 'vehicleFinancing.annualMileage'
                }),
                createDetail({
                    name: disclosures.term.name,
                    format: numberFormat,
                    symbol: MO,
                    key: 'vehicleFinancing.term'
                }),
                createDetail({ name: disclosures.residualAmount.name, key: 'vehicleFinancing.residualAmount' })
            ],
            style: { noBorderBottom: 1 }
        }),
        //Amounts Due At Lease Signing or Delivery
        createDetail({
            mainTitle: commonTitles.AMOUNT_DUE_TITLE,
            name: AMOUNTS_DUE_AT_LEASE_SIGNING,
            key: 'dueAtSigning.totalAmountsDueAtLeaseSigningOrDelivery',
            details: [
                createDetail({ name: FIRST_MONTHLY_PAYMENT, key: 'dueAtSigning.monthlyPayment' }),
                createDetail({
                    name: disclosures.totalCapitalizedCostReduction.name,
                    key: 'dueAtSigning.capCostReduction'
                }),
                createDetail({ name: OTHER_UPFRONT_COSTS, key: 'dueAtSigning.otherUpfrontCosts' })
            ],
            style: { noBorderBottom: 1 }
        }),
        //How the Amounts due at Lease signing or Delivery
        createDetail({
            name: HOW_AMOUNTS_DUE_WILL_BE_PAID,
            key: 'dueAtSigning.totalAmountsDueAtLeaseSigning',
            details: [
                createDetail({ name: disclosures.currentAvailableIncentives.name, key: 'dueAtSigning.totalRebates' }),
                createDetail({ name: NET_TRADE_IN_AMOUNT, key: 'dueAtSigning.netTradeInAllowance' }),
                createDetail({ name: AMOUNT_PAID_IN_CASH, key: 'dueAtSigning.amountsPaidInCash' })
            ],
            style: { noBorderBottom: 1 }
        })
    ]
    return leaseDetails
}

export const protectionPlanAccordion = (insuranceWarranties) => {
    let protectionPlanAccordionDetails = (insuranceWarranties || []).reduce(
        (accumulator, { provider = '', product = '', premium }) => {
            if (premium) {
                const obj = {}
                product = product.replace(/([A-Z])/g, ' $1').trim()
                const productName = product.charAt(0).toUpperCase() + product.slice(1) // for capitalize first letter of text
                const providerName = `${provider} ${productName}`
                obj.name = providerName
                obj.value = numberToCurrencyFormat(premium)
                obj.disclosure = null
                obj.details = []
                obj.style = []
                accumulator.push(obj)
            }
            return accumulator
        },
        []
    )

    return protectionPlanAccordionDetails
}

export const processCashDownPayment = (detail, source, acc, orderStart, index) => {
    if (detail?.name === disclosures.cashDownPayment.name) {
        const totalDownPaymentvalue =
            (source?.cashDownPayment ?? 0) + (source?.creditCardDownPayment ?? 0) + (source?.otherRebate ?? 0)
        const totalDownPayment = `-${numberToCurrencyFormat(totalDownPaymentvalue)}`
        if (totalDownPaymentvalue && totalDownPayment) {
            acc.push({
                name: detail?.name, // disclosure for DownPayment
                value: totalDownPayment,
                disclosure: disclosures.cashDownPayment.order,
                order: orderStart + index,
                details: detail.details ? createAccordionData(detail.details, source) : [],
                style: detail.style ?? []
            })
        }
    }
}

export const processLeaseVehiclePaymentInfo = (detail, source, acc, orderStart, index, commonTitles) => {
    if (detail?.mainTitle === commonTitles.LEASE_VEHCILE_PAYMENT_INFO) {
        const vehicleFinancing = source?.vehicleFinancing
        if (vehicleFinancing) {
            acc.push({
                mainTitle: commonTitles.LEASE_VEHCILE_PAYMENT_INFO,
                name: null, // disclosure for Vehicle Lease Payment
                value: null,
                showAlways: true,
                disclosure: null,
                order: orderStart + index,
                details: detail.details ? createAccordionData(detail.details, source) : [],
                style: detail.style ?? []
            })
        }
    }
}

export const disclosureOrder = (key, name) => {
    if (name === CASH_DOWN_PAYMENT || name === CREDIT_CARD_DOWN_PAYMENT || name === 'otherRebateDesc') {
        return null
    } else if (name === disclosures.insuranceWarranties.name) {
        return disclosures.insuranceWarranties.order
    } else if (name === disclosures.totalCapitalizedCostReduction.name) {
        return disclosures.totalCapitalizedCostReduction.order
    }
    return key
}

export const processValue = (detail, source, acc, orderStart, index, keys, value) => {
    if (value) {
        const lastKey = keys[keys.length - 1]
        let accordionDetails
        if (
            detail?.name === disclosures.insuranceWarranties.name &&
            source?.insuranceWarranties?.insuranceWarranties?.length > 0
        ) {
            accordionDetails = protectionPlanAccordion(source?.insuranceWarranties?.insuranceWarranties)
        } else {
            accordionDetails = detail.details ? createAccordionData(detail.details, source) : []
        }

        let isincludeAXZPlan =
            detail?.name === disclosures.dealerSellingPrice.name
                ? source?.pdsDealerSellingPrice?.employeePricingIndicator
                : false

        acc.push({
            mainTitle: detail.mainTitle ?? null,
            order: orderStart + index,
            name: formatDynamiceName(detail.name, source),
            value: formatValue(detail.preFormat, detail.format, value, detail.symbol, detail.sign),
            disclosure: disclosureOrder(disclosures[lastKey]?.order, detail.name),
            includeAXZPlan: isincludeAXZPlan,
            includeAXZPrice: isincludeAXZPlan
                ? formatValue(detail.preFormat, detail.format, source.totalAXZPlanPricing, detail.symbol, detail.sign)
                : 0,
            details: accordionDetails,
            style: detail.style ?? []
        })
    }
}

export const createAccordionData = (details, source, orderStart = 1) => {
    return details.reduce((acc, detail, index) => {
        const keys = detail?.key ? detail.key.split('.') : null
        const value = keys ? keys.reduce((obj, key) => obj?.[key], source) : null

        processCashDownPayment(detail, source, acc, orderStart, index)
        processLeaseVehiclePaymentInfo(detail, source, acc, orderStart, index, commonTitles)
        processValue(detail, source, acc, orderStart, index, keys, value)

        return acc
    }, [])
}

export const financeBasedMapping = (transactionType) => {
    if (transactionType === 'Lease') {
        return leaseMappingDetails()
    }
    return nonLeaseMappingDetails(transactionType === 'Cash' ? 'Cash' : undefined)
}

export const formatAccordion = (response) => {
    const financeMapping = financeBasedMapping(response?.transactionType)
    const accordion = createAccordionData(financeMapping, response)
    const filterAccordion = accordion.filter(
        (data) => data.mainTitle === commonTitles.LEASE_VEHCILE_PAYMENT_INFO || data.value
    )
    return filterAccordion
}

export const getBoldBorderTopStyle = (purchageDetails) => {
    return purchageDetails?.style?.boldBorderTop
        ? {
              borderTop: '2px solid #000000',
              marginTop: '-17px',
              pt: 4
          }
        : {}
}

export const getFinalTotalStyle = (purchageDetails) => {
    return purchageDetails?.style?.finalTotal
        ? {
              backgroundImage: 'linear-gradient(128deg, #F5F6F7 35.5%, #FEFEFE 140.67%)',
              borderRadius: 4,
              p: { xs: '24px 16px 8px', smd: '24px 24px 8px' },
              mx: { xs: '-12px', smd: '-24px' }
          }
        : {}
}

export const getTypographyStyle = (purchageDetails) => {
    const boldTypographyStyle = purchageDetails?.style?.boldBorderTop
        ? { xs: 'boldBody1', smd: 'boldSubTitle' }
        : { xs: 'regularBody1', smd: 'regularSubTitle' }

    return purchageDetails?.style?.finalTotal
        ? { xs: 'mediumBody1', smd: 'mediumSubTitle', md: 'mediumTitle' }
        : boldTypographyStyle
}

export const getBorderTopStyle = (purchageDetails) => {
    return purchageDetails?.style?.borderTop
        ? {
              borderTop: '2px solid #000000',
              marginTop: '-17px',
              pt: 4,
              pb: 0
          }
        : {}
}

export const getNameElements = (customer, block, title, details) => {
    const blockName = block ? `${block}-${details}` : `${title}-${details}`
    return customer ? `your-information-${details}` : blockName
}
